import type { RegisteredCustomField } from 'src/services/encompass/getRegisteredCustomFields';
import {
  isCustomEncompassFieldKind,
  type EncompassFieldKind,
} from 'src/services/encompass/getEncompassCustomFields';
import { MappingSource } from './MappingsTable/utils/mappingsConstants';

export interface EncompassSelectableField {
  id: string;
  description: string;
  dataType: string;
  fieldKind: EncompassFieldKind;
  instanceId?: string;
  jsonPath?: string;
  contractPath?: string;
  format?: string;
  maxLength?: number;
  options?: Array<{ value: string; text: string }>;
  isCalculated?: boolean | null;
  references?: Array<{ externalFieldId: string; name: string }>;
}

export function registeredCustomFieldOptions(
  rows: RegisteredCustomField[] = [],
): EncompassSelectableField[] {
  const fields = new Map<string, EncompassSelectableField>();
  const sorted = [...rows].sort(
    (a, b) =>
      b.updatedAt.localeCompare(a.updatedAt) || a.id.localeCompare(b.id),
  );
  for (const row of sorted) {
    const metadata = row.metadata;
    if (
      row.source !== MappingSource.EncompassCustomFields ||
      !isCustomEncompassFieldKind(metadata?.fieldKind) ||
      !metadata.source_field_id ||
      !metadata.instanceId
    )
      continue;
    const key = JSON.stringify([metadata.instanceId, metadata.source_field_id]);
    let field = fields.get(key);
    if (!field) {
      field = {
        ...metadata,
        id: metadata.source_field_id,
        description: metadata.description ?? row.name,
        options: metadata.options ?? undefined,
        references: [],
      };
      fields.set(key, field);
    }
    field.references!.push({ externalFieldId: row.id, name: row.name });
  }
  return [...fields.values()];
}

export type CustomFieldMetadata = {
  dataType: string;
  jsonPath: string;
  options: { value: string; label: string }[];
};

export function buildCustomFieldOptions(
  customFields: EncompassSelectableField[],
): { value: string; label: string }[] {
  return customFields.map((field) => ({
    value: field.id,
    label: field.description ? `${field.id} — ${field.description}` : field.id,
  }));
}

export function buildCustomFieldMetadataMap(
  customFields: EncompassSelectableField[],
): Map<string, CustomFieldMetadata> {
  const metadata = new Map<string, CustomFieldMetadata>();
  for (const field of customFields) {
    const fieldId = field.id.trim();
    if (!fieldId) continue;
    const seen = new Set<string>();
    metadata.set(fieldId, {
      dataType: field.dataType ?? '',
      jsonPath: field.jsonPath ?? '',
      options: (field.options ?? []).flatMap((option) => {
        const value = option.value?.trim() ?? '';
        if (!value || seen.has(value)) return [];
        seen.add(value);
        return [{ value, label: option.text?.trim() || value }];
      }),
    });
  }
  return metadata;
}

export function resolveFieldDataTypeForKind(
  kind: EncompassFieldKind,
  fieldId: string,
  customMetadataById: Map<string, CustomFieldMetadata>,
  fieldDataTypeById: Map<string, string>,
): string {
  return isCustomEncompassFieldKind(kind)
    ? (customMetadataById.get(fieldId.trim())?.dataType ?? '')
    : (fieldDataTypeById.get(fieldId.trim()) ?? '');
}

export function resolveFieldJsonPathForKind(
  kind: EncompassFieldKind,
  fieldId: string,
  customMetadataById: Map<string, CustomFieldMetadata>,
): string {
  return isCustomEncompassFieldKind(kind)
    ? (customMetadataById.get(fieldId.trim())?.jsonPath ?? '')
    : '';
}

export function resolveFieldValueOptionsForKind(
  kind: EncompassFieldKind,
  fieldId: string,
  customMetadataById: Map<string, CustomFieldMetadata>,
  getStandardOptions: (fieldId: string) => { value: string; label: string }[],
): { value: string; label: string }[] {
  return isCustomEncompassFieldKind(kind)
    ? (customMetadataById.get(fieldId.trim())?.options ?? [])
    : getStandardOptions(fieldId);
}

export function isDateFieldForKind(
  kind: EncompassFieldKind,
  fieldId: string,
  customMetadataById: Map<string, CustomFieldMetadata>,
  isStandardDateTimeField: (
    fieldId: string,
    fallbackDataType?: string,
  ) => boolean,
  fallbackDataType?: string,
): boolean {
  const dataType = (
    customMetadataById.get(fieldId.trim())?.dataType ??
    fallbackDataType ??
    ''
  )
    .trim()
    .toLowerCase();
  return isCustomEncompassFieldKind(kind)
    ? dataType === 'datetime' || dataType === 'date'
    : isStandardDateTimeField(fieldId, fallbackDataType);
}
